import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

/**
 * ConsultationReportPDF Component
 * 
 * Extracts the PDF generation and consultation report logic from the appointment details form.
 * Handles PDF creation, download, and printing functionality for consultation reports.
 * Maintains all existing PDF formatting and data inclusion features.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formikValues - Formik values object for PDF generation
 * @param {Object} props.MedicalDetail - Medical details data
 * @param {Object} props.CcPaperDetail - Chief complaint paper details
 * @param {Object} props.AppointmentData - Appointment data
 * @param {string} props.PatientName - Patient name
 * @param {string} props.PatientPhoneNo - Patient phone number
 * @param {string} props.PatientGender - Patient gender
 * @param {string} props.PatientBirthDate - Patient birth date
 * @param {string} props.PatientRegistrationNo - Patient registration number
 * @param {string} props.PatientDateOfConsultation - Patient consultation date
 * @param {string} props.PatientConsultatingDoctor - Consulting doctor name
 * @param {string} props.tempChiefComplaint - Temporary chief complaint
 * @param {string} props.treatmentDetails - Treatment details
 */
const ConsultationReportPDF = ({
  formikValues,
  MedicalDetail,
  CcPaperDetail,
  AppointmentData,
  PatientName,
  PatientPhoneNo,
  PatientGender,
  PatientBirthDate,
  PatientRegistrationNo,
  PatientDateOfConsultation,
  PatientConsultatingDoctor,
  tempChiefComplaint,
  treatmentDetails,
}) => {
  /**
   * PDF Generation Function - accepts formik values as parameter
   */


  const generateConsultationReport = (formikValues = null) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Use formik values if provided, otherwise use saved medical details
    const dataSource = formikValues || MedicalDetail?.[0] || {};

    // Add Hospital Logo and Header
    doc.setFillColor(255, 216, 168); // Orange color
    doc.rect(0, 0, pageWidth, 80, 'F');

    // Add Logo Image at top center
    const logoImg = new Image();
    logoImg.src = '/prakruti-logo.png';
    try {
      doc.addImage(logoImg, 'PNG', pageWidth / 2 - 20, 5, 40, 30);
    } catch (error) {
      console.log('Logo not loaded, continuing without it');
    }

    // Hospital Name
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('PRAKRUTI AYURVEDIC HEALTH CENTRE AND HOSPITAL', pageWidth / 2, 45, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('(A unit of Prakruti Ayurvedic Health Resorts Private Limited)', pageWidth / 2, 55, { align: 'center' });
    doc.text('Address: A/P Yevateshwar, Satara 415002, Maharashtra, India', pageWidth / 2, 60, { align: 'center' });
    doc.text('Reg Office: 177, Budhwar Peth, Parale Sankulan, Satara-415002, Maharashtra, India.', pageWidth / 2, 65, { align: 'center' });
    doc.text('Email: feelbetter@prakruti.health   Phone: 9175242908', pageWidth / 2, 70, { align: 'center' });

    yPosition = 70;
    yPosition += 15;

    // Patient Information Section
    doc.setFillColor(2, 62, 26); // Dark Green color
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('PATIENT INFORMATION', 15, yPosition + 7);
    yPosition += 15;

    // Patient Details
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const patientDetails = [
      ['Patient Name:', PatientName || '---'],
      ['Phone:', PatientPhoneNo || '---'],
      ['Gender:', PatientGender || '---'],
      ['Birth Date:', (PatientBirthDate && moment(PatientBirthDate).format('DD/MM/YYYY')) || '---'],
      ['UHID:', PatientRegistrationNo || '---'],
      ['Date of Booking:', (PatientDateOfConsultation && moment(PatientDateOfConsultation, 'DD/MM/YYYY').format('DD/MM/YYYY')) || '---'],
      ['Consulting Doctor:', PatientConsultatingDoctor || '---'],
    ];

    patientDetails.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 15, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 70, yPosition);
      yPosition += 7;
    });

    yPosition += 5;

    // Treatment
    doc.setFillColor(243, 244, 246);
    doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('TREATMENT:', 15, yPosition + 6);
    yPosition += 12;
    doc.setFont('helvetica', 'normal');
    const treatmentText = doc.splitTextToSize(treatmentDetails || '---', pageWidth - 30);
    doc.text(treatmentText, 15, yPosition);
    yPosition += treatmentText.length * 7 + 5;

    // Chief Complaint
    if (yPosition > pageHeight - 40) {
      doc.addPage();
      yPosition = 20;
    }
    doc.setFillColor(243, 244, 246);
    doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('CHIEF COMPLAINT WITH DURATION:', 15, yPosition + 6);
    yPosition += 12;
    doc.setFont('helvetica', 'normal');
    const chiefComplaintFromState = CcPaperDetail?.personalDetails?.filter(
      item => item?.personalDetilsKey === 'ChiefComplaints'
    )[0]?.value;
    const chiefComplaint = chiefComplaintFromState || tempChiefComplaint || '---';
    const complaintText = doc.splitTextToSize(chiefComplaint, pageWidth - 30);
    doc.text(complaintText, 15, yPosition);
    yPosition += complaintText.length * 7 + 10;

    // Medical Details Section
    if (dataSource && Object.keys(dataSource).length > 0) {
      const medical = dataSource;

      // Diagnosis
      if (medical.diagnosis && medical.diagnosis.length > 0 && medical.diagnosis[0].diagnosis) {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(2, 62, 26);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text('DIAGNOSIS', 15, yPosition + 7);
        yPosition += 15;
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        medical.diagnosis.forEach((diag, index) => {
          if (diag.diagnosis) {
            doc.text(`${index + 1}. ${diag.diagnosis}${diag.other ? ' - ' + diag.other : ''}`, 15, yPosition);
            yPosition += 7;
          }
        });
        yPosition += 5;
      }

      // Recommended Medicines
      if (medical.recomendedMedicines && medical.recomendedMedicines.length > 0 && medical.recomendedMedicines[0].medicineName) {
        if (yPosition > pageHeight - 60) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(2, 62, 26);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text('RECOMMENDED MEDICINES', 15, yPosition + 7);
        yPosition += 15;
        doc.setTextColor(0, 0, 0);

        const medicineTableData = medical.recomendedMedicines
          .filter(med => med.medicineName)
          .map((med, index) => [
            index + 1,
            med.medicineName || '---',
            med.days || '---',
            Array.isArray(med.whenToTake) ? med.whenToTake.join(', ') : (med.whenToTake || '---'),
            Array.isArray(med.howMuch) ? med.howMuch.join(', ') : (med.howMuch || '---'),
            med.anupan || '---',
            med.course || '---',
          ]);

        if (medicineTableData.length > 0) {
          autoTable(doc, {
            startY: yPosition,
            head: [['#', 'Medicine Name', 'Days', 'When to Take', 'How Much', 'Anupan', 'Course']],
            body: medicineTableData,
            theme: 'grid',
            headStyles: { fillColor: [2, 62, 26], textColor: [255, 255, 255] },
            styles: { fontSize: 8 },
            margin: { left: 10, right: 10 },
          });
          yPosition = doc.lastAutoTable.finalY + 10;
        }
      }

      // Laboratory Investigations
      if (medical.laboratory && medical.laboratory.length > 0) {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(2, 62, 26);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text('LABORATORY INVESTIGATIONS', 15, yPosition + 7);
        yPosition += 15;
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');

        medical.laboratory.forEach((lab, index) => {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }

          // Check if lab is an object with category (new structure) or string (old structure)
          if (typeof lab === 'object' && lab.category) {
            doc.setFont('helvetica', 'bold');
            doc.text(`${lab.category}:`, 15, yPosition);
            yPosition += 7;
            doc.setFont('helvetica', 'normal');
            if (lab.options && lab.options.length > 0) {
              lab.options.forEach((option) => {
                doc.text(`  • ${option}`, 20, yPosition);
                yPosition += 6;
              });
            }
            yPosition += 3;
          } else {
            // Old structure - simple string
            doc.text(`${index + 1}. ${lab}`, 15, yPosition);
            yPosition += 7;
          }
        });
        yPosition += 5;

        // Laboratory Note
        if (medical.laboratoryNote) {
          doc.setFont('helvetica', 'bold');
          doc.text('Laboratory Note:', 15, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          const labNoteText = doc.splitTextToSize(medical.laboratoryNote, pageWidth - 30);
          doc.text(labNoteText, 15, yPosition);
          yPosition += labNoteText.length * 7 + 5;
        }
      }

      // Advice
      if (medical.advice && medical.advice.length > 0) {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(243, 244, 246);
        doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('ADVICE:', 15, yPosition + 6);
        yPosition += 12;
        doc.setFont('helvetica', 'normal');
        medical.advice.forEach((adv, index) => {
          doc.text(`${index + 1}. ${adv}`, 15, yPosition);
          yPosition += 7;
        });
        yPosition += 5;
      }

      // Next Consultation Date
      if (medical.nextConsultationDate) {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFont('helvetica', 'bold');
        doc.text('Next Consultation Date:', 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(dayjs(medical.nextConsultationDate).format('DD/MM/YYYY'), 70, yPosition);
        yPosition += 10;
      }

      // Treatment
      if (medical.treatment && medical.treatment.length > 0 && medical.treatment[0].treatmentName) {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(243, 244, 246);
        doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('TREATMENT PLAN:', 15, yPosition + 6);
        yPosition += 12;
        doc.setFont('helvetica', 'normal');
        medical.treatment.forEach((treat, index) => {
          if (treat.treatmentName) {
            const treatText = `${index + 1}. ${treat.treatmentName}${treat.other ? ' - ' + treat.other : ''}${treat.treatmentsDay ? ' (Day ' + treat.treatmentsDay + ')' : ''}`;
            const splitText = doc.splitTextToSize(treatText, pageWidth - 30);
            doc.text(splitText, 15, yPosition);
            yPosition += splitText.length * 7;
          }
        });
        yPosition += 5;
      }

      // Solution
      if (medical.solution && medical.solution.length > 0 && medical.solution[0].solutionName) {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(243, 244, 246);
        doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('SOLUTION:', 15, yPosition + 6);
        yPosition += 12;
        doc.setFont('helvetica', 'normal');
        medical.solution.forEach((sol, index) => {
          if (sol.solutionName) {
            const solText = `${index + 1}. ${sol.solutionName}${sol.other ? ' - ' + sol.other : ''}${sol.treatmentsDay ? ' (Day ' + sol.treatmentsDay + ')' : ''}`;
            const splitText = doc.splitTextToSize(solText, pageWidth - 30);
            doc.text(splitText, 15, yPosition);
            yPosition += splitText.length * 7;
          }
        });
        yPosition += 5;
      }

      // Examination Findings Section
      const hasExaminationData = medical.prakruti?.length > 0 || medical.nadi?.length > 0 ||
        medical.jivha?.length > 0 || medical.eyes || medical.nails;

      if (hasExaminationData) {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(2, 62, 26);
        doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text('EXAMINATION FINDINGS', 15, yPosition + 7);
        yPosition += 15;
        doc.setTextColor(0, 0, 0);

        // Prakruti
        if (medical.prakruti && medical.prakruti.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text('Prakruti:', 15, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(medical.prakruti.join(', '), 50, yPosition);
          yPosition += 7;
        }

        // Nadi
        if (medical.nadi && medical.nadi.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text('Nadi:', 15, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(medical.nadi.join(', '), 50, yPosition);
          yPosition += 7;
        }

        // Jivha
        if (medical.jivha && medical.jivha.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text('Jivha:', 15, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(medical.jivha.join(', '), 50, yPosition);
          yPosition += 7;
        }

        // Eyes
        if (medical.eyes) {
          doc.setFont('helvetica', 'bold');
          doc.text('Eyes:', 15, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(medical.eyes, 50, yPosition);
          yPosition += 7;
        }

        // Nails
        if (medical.nails) {
          doc.setFont('helvetica', 'bold');
          doc.text('Nails:', 15, yPosition);
          doc.setFont('helvetica', 'normal');
          const nailsText = doc.splitTextToSize(medical.nails, pageWidth - 60);
          doc.text(nailsText, 50, yPosition);
          yPosition += nailsText.length * 7;
        }

        yPosition += 5;
      }

      // Root Cause
      if (medical.rootCause && medical.rootCause.length > 0) {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFont('helvetica', 'bold');
        doc.text('Root Cause:', 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(medical.rootCause.join(', '), 60, yPosition);
        yPosition += 10;
      }

      // Upshay & Anupshay
      if (medical.upshay || medical.anupshay) {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }
        if (medical.upshay) {
          doc.setFont('helvetica', 'bold');
          doc.text('Upshay:', 15, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(medical.upshay, 60, yPosition);
          yPosition += 7;
        }
        if (medical.anupshay) {
          doc.setFont('helvetica', 'bold');
          doc.text('Anupshay:', 15, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(medical.anupshay, 60, yPosition);
          yPosition += 7;
        }
        yPosition += 3;
      }

      // Panchakarma
      if (medical.panchakrama && medical.panchakrama.length > 0) {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFont('helvetica', 'bold');
        doc.text('Panchakarma:', 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(medical.panchakrama.join(', '), 60, yPosition);
        yPosition += 10;
      }

      // Virechan
      if (medical.virechan) {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFont('helvetica', 'bold');
        doc.text('Virechan:', 15, yPosition);
        doc.setFont('helvetica', 'normal');
        const virechanText = doc.splitTextToSize(medical.virechan, pageWidth - 65);
        doc.text(virechanText, 60, yPosition);
        yPosition += virechanText.length * 7 + 5;
      }

      // Do's and Don'ts
      if ((medical.dos && medical.dos.length > 0) || (medical.donts && medical.donts.length > 0)) {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(243, 244, 246);
        doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('LIFESTYLE RECOMMENDATIONS:', 15, yPosition + 6);
        yPosition += 12;

        if (medical.dos && medical.dos.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 128, 0);
          doc.text('Do\'s:', 15, yPosition);
          yPosition += 7;
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'normal');
          medical.dos.forEach((doItem) => {
            const doText = doc.splitTextToSize(`• ${doItem}`, pageWidth - 35);
            doc.text(doText, 20, yPosition);
            yPosition += doText.length * 7;
          });
          yPosition += 3;
        }

        if (medical.donts && medical.donts.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(220, 38, 38);
          doc.text('Don\'ts:', 15, yPosition);
          yPosition += 7;
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'normal');
          medical.donts.forEach((dontItem) => {
            const dontText = doc.splitTextToSize(`• ${dontItem}`, pageWidth - 35);
            doc.text(dontText, 20, yPosition);
            yPosition += dontText.length * 7;
          });
          yPosition += 3;
        }
        yPosition += 2;
      }

      // Exercise
      if (medical.exercise && medical.exercise.length > 0) {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFont('helvetica', 'bold');
        doc.text('Exercise:', 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(medical.exercise.join(', '), 60, yPosition);
        yPosition += 10;
      }

      // Doctor Notes
      if (medical.doctorNotes && medical.doctorNotes.length > 0 && medical.doctorNotes[0]) {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFillColor(243, 244, 246);
        doc.rect(10, yPosition, pageWidth - 20, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('DOCTOR NOTES:', 15, yPosition + 6);
        yPosition += 12;
        doc.setFont('helvetica', 'normal');
        medical.doctorNotes.forEach((note, index) => {
          if (note) {
            const noteText = doc.splitTextToSize(note, pageWidth - 30);
            doc.text(noteText, 15, yPosition);
            yPosition += noteText.length * 7;
          }
        });
      }
    }

    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} of ${totalPages} | Generated on ${moment().format('DD/MM/YYYY HH:mm')}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    const fileName = `Consultation_Report_${PatientName || 'Patient'}_${moment().format('DDMMYYYY')}.pdf`;
    doc.save(fileName);
  };

  return (
    <div
      onClick={() => generateConsultationReport(formikValues)}
      style={{
        position: 'fixed',
        bottom: '30px',
        right: '30px',
        width: '60px',
        height: '60px',
        backgroundColor: '#4f46e5',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.4)',
        transition: 'all 0.3s ease',
        zIndex: 1000,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = '#4338ca';
        e.currentTarget.style.transform = 'scale(1.1)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(79, 70, 229, 0.6)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = '#4f46e5';
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.4)';
      }}
      title="Download Consultation Report"
    >
      <FeatherIcon icon="download" style={{ color: 'white', width: '24px', height: '24px' }} />
    </div>
  );
};

export default ConsultationReportPDF;